import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useFooterSettings } from "@/hooks/useFooterSettings";
import { FooterLink } from "@/types/footer.types";
import { 
  ArrowDown, 
  ArrowUp, 
  Edit2, 
  ExternalLink, 
  Loader2, 
  Plus, 
  Save, 
  Star, 
  Trash2, 
  X 
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface FooterLinksManagerProps {
  section: 'resources' | 'useful_links';
}

export function FooterLinksManager({ section }: FooterLinksManagerProps) {
  const { allFooterLinks, addLink, updateLink, deleteLink, isUpdating } = useFooterSettings();
  
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState<FooterLink | null>(null);
  
  const [formData, setFormData] = useState({
    label: "",
    url: "",
    is_external: false,
    is_highlighted: false,
  });

  const sectionLinks = allFooterLinks
    .filter(link => link.section === section)
    .sort((a, b) => a.display_order - b.display_order);

  const handleOpenAdd = () => {
    setFormData({ label: "", url: "", is_external: false, is_highlighted: false });
    setIsAddDialogOpen(true);
  };

  const handleOpenEdit = (link: FooterLink) => {
    setSelectedLink(link);
    setFormData({
      label: link.label,
      url: link.url,
      is_external: link.is_external,
      is_highlighted: link.is_highlighted,
    });
    setIsEditDialogOpen(true);
  };

  const handleOpenDelete = (link: FooterLink) => {
    setSelectedLink(link);
    setIsDeleteDialogOpen(true);
  };

  const handleAdd = () => {
    const maxOrder = sectionLinks.length > 0 
      ? Math.max(...sectionLinks.map(l => l.display_order)) 
      : 0;

    addLink({
      section,
      label: formData.label,
      url: formData.url,
      is_external: formData.is_external,
      is_highlighted: formData.is_highlighted,
      display_order: maxOrder + 1,
      is_active: true,
    });
    setIsAddDialogOpen(false);
  };

  const handleEdit = () => {
    if (!selectedLink) return;
    updateLink({
      id: selectedLink.id,
      label: formData.label,
      url: formData.url,
      is_external: formData.is_external,
      is_highlighted: formData.is_highlighted,
    });
    setIsEditDialogOpen(false);
  };

  const handleDelete = () => {
    if (!selectedLink) return;
    deleteLink(selectedLink.id);
    setIsDeleteDialogOpen(false);
  };

  const handleToggleActive = (link: FooterLink) => {
    updateLink({
      id: link.id,
      is_active: !link.is_active,
    });
  };

  const handleMoveUp = (link: FooterLink, index: number) => {
    if (index === 0) return;
    const prevLink = sectionLinks[index - 1];
    updateLink({ id: link.id, display_order: prevLink.display_order });
    updateLink({ id: prevLink.id, display_order: link.display_order });
  };

  const handleMoveDown = (link: FooterLink, index: number) => {
    if (index === sectionLinks.length - 1) return;
    const nextLink = sectionLinks[index + 1];
    updateLink({ id: link.id, display_order: nextLink.display_order });
    updateLink({ id: nextLink.id, display_order: link.display_order });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={handleOpenAdd} size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Adicionar Link
        </Button>
      </div>

      {sectionLinks.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          Nenhum link cadastrado nesta seção.
        </div>
      ) : (
        <div className="space-y-2">
          {sectionLinks.map((link, index) => (
            <div
              key={link.id}
              className={`flex items-center justify-between p-3 rounded-lg border ${
                link.is_active ? 'bg-background' : 'bg-muted/50 opacity-60'
              }`}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="flex flex-col gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => handleMoveUp(link, index)}
                    disabled={index === 0 || isUpdating}
                  >
                    <ArrowUp className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => handleMoveDown(link, index)}
                    disabled={index === sectionLinks.length - 1 || isUpdating}
                  >
                    <ArrowDown className="h-3 w-3" />
                  </Button>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{link.label}</span>
                    {link.is_highlighted && (
                      <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                    )}
                    {link.is_external && (
                      <ExternalLink className="h-3 w-3 text-muted-foreground" />
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground truncate block">
                    {link.url}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={link.is_active}
                  onCheckedChange={() => handleToggleActive(link)}
                  disabled={isUpdating}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleOpenEdit(link)}
                  disabled={isUpdating}
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleOpenDelete(link)}
                  disabled={isUpdating}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Link</DialogTitle>
            <DialogDescription>
              Adicione um novo link à seção {section === 'resources' ? 'Recursos' : 'Links Úteis'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="add-label">Texto do Link</Label>
              <Input
                id="add-label"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="Ex: Guia de Carreira"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-url">URL</Label>
              <Input
                id="add-url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="Ex: /guias/carreira ou https://..."
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="add-external">Link Externo (abre em nova aba)</Label>
              <Switch
                id="add-external"
                checked={formData.is_external}
                onCheckedChange={(checked) => setFormData({ ...formData, is_external: checked })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="add-highlighted">Destacar Link</Label>
              <Switch
                id="add-highlighted"
                checked={formData.is_highlighted}
                onCheckedChange={(checked) => setFormData({ ...formData, is_highlighted: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAdd} disabled={!formData.label || !formData.url || isUpdating}>
              {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Adicionar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Link</DialogTitle>
            <DialogDescription>
              Edite as informações do link
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-label">Texto do Link</Label>
              <Input
                id="edit-label"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-url">URL</Label>
              <Input
                id="edit-url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="edit-external">Link Externo (abre em nova aba)</Label>
              <Switch
                id="edit-external"
                checked={formData.is_external}
                onCheckedChange={(checked) => setFormData({ ...formData, is_external: checked })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="edit-highlighted">Destacar Link</Label>
              <Switch
                id="edit-highlighted"
                checked={formData.is_highlighted}
                onCheckedChange={(checked) => setFormData({ ...formData, is_highlighted: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleEdit} disabled={!formData.label || !formData.url || isUpdating}>
              {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o link "{selectedLink?.label}"? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
